/**
 * @file Pressable atom — the interaction primitive (RFC §3).
 * @description Replaces raw RN Pressable/TouchableOpacity in app code.
 * Forwards testID and accessibility props (part of the atom API contract).
 * @module components/atoms/Pressable
 */
import React from "react";

import { Pressable as UIPressable } from "@/components/ui/pressable";

import { deriveAriaFromAccessibilityState } from "../accessibilityState";
import type { WithUnsafeStyle } from "../types";

/**
 *
 */
type UIPressableProps = React.ComponentProps<typeof UIPressable>;

/**
 *
 */
export interface PressableProps
  extends
    Omit<UIPressableProps, "style">,
    WithUnsafeStyle<UIPressableProps["style"]> {}

const Pressable = React.forwardRef<
  React.ComponentRef<typeof UIPressable>,
  PressableProps
>(function Pressable({ UNSAFE_style, accessibilityState, ...props }, ref) {
  // `accessibilityState` is forwarded for NATIVE and the derived `aria-*` props
  // are added for WEB, which drops the object form (SE-6297). `props` spreads
  // LAST so an explicit `aria-*` from the caller wins over the derived value.
  return (
    <UIPressable
      ref={ref}
      style={UNSAFE_style}
      accessibilityState={accessibilityState}
      {...deriveAriaFromAccessibilityState(accessibilityState)}
      {...props}
    />
  );
});

Pressable.displayName = "Pressable";

export { Pressable };
